/**
 * Web Push 권한 요청 모달.
 *
 * 노출 트리거 (OR):
 *  1. 북마크 추가 직후 ('cb:bookmark-added' 커스텀 이벤트)
 *  2. 방문 횟수 ≥ 3회 (cb:visitCount 활용)
 *
 * AND 조건:
 *  - 마지막 dismiss로부터 14일 경과
 *  - Notification.permission === 'default'
 *  - isPushSupported() === true
 *
 * 영구지침: docs/develop/pwa/pwa-spec.md §7.2
 */
import { useEffect, useRef, useState } from 'react'
import { Bell, X } from 'lucide-react'
import {
  getNotificationPermission,
  isPushSupported,
  subscribeToPush,
} from '@/lib/pwa/pushSubscription'
import { useAuthStore } from '@/stores/authStore'
import { safeLocalStorage } from '@/lib/safeStorage'

const STORAGE_DISMISSED_AT = 'cb:pushPromptDismissedAt'
const STORAGE_VISIT_COUNT = 'cb:visitCount'
const DISMISS_COOLDOWN_MS = 14 * 24 * 60 * 60 * 1000 // 14d
const MIN_VISIT_COUNT = 3

function isRecentlyDismissed(): boolean {
  const at = safeLocalStorage.getItem(STORAGE_DISMISSED_AT)
  if (!at) return false
  return Date.now() - Number(at) < DISMISS_COOLDOWN_MS
}

function getVisitCount(): number {
  return Number(safeLocalStorage.getItem(STORAGE_VISIT_COUNT) ?? '0')
}

function shouldShow(): boolean {
  if (!isPushSupported()) return false
  if (getNotificationPermission() !== 'default') return false
  if (isRecentlyDismissed()) return false
  return true
}

/** 푸시 구독 API는 인증 필수 → 로그인 상태에서만 프롬프트 표시 */
function isLoggedIn(): boolean {
  return !!safeLocalStorage.getItem('accessToken')
}

export default function PushPermissionPrompt() {
  const [visible, setVisible] = useState(false)
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const dialogRef = useRef<HTMLDivElement>(null)
  const enableButtonRef = useRef<HTMLButtonElement>(null)

  const isAuthenticated = useAuthStore((s) => s.isAuthenticated)

  // 노출 조건 (영구지침 §7.2 OR 조건):
  //  1. 북마크 추가 직후 (cb:bookmark-added 커스텀 이벤트)
  //  2. 방문 횟수 ≥ 3회 (5초 지연)
  // 추가 조건: 로그인 상태여야 함 (구독 API가 인증 필수)
  useEffect(() => {
    if (!shouldShow()) return
    if (!isAuthenticated && !isLoggedIn()) return

    // 조건 2: 방문 횟수 기반 — 5초 지연 후 노출 시도
    let timer: ReturnType<typeof setTimeout> | undefined
    if (getVisitCount() >= MIN_VISIT_COUNT) {
      timer = setTimeout(() => {
        if (shouldShow()) setVisible(true)
      }, 5000)
    }

    // 조건 1: 북마크 추가 이벤트 — 즉시 노출 (방문 횟수와 무관)
    const bookmarkHandler = () => {
      if (shouldShow()) setVisible(true)
    }
    window.addEventListener('cb:bookmark-added', bookmarkHandler)

    return () => {
      if (timer) clearTimeout(timer)
      window.removeEventListener('cb:bookmark-added', bookmarkHandler)
    }
  }, [])

  // 모달 열림 시 a11y: ESC 닫기 + 초기 포커스 + 간이 focus trap
  useEffect(() => {
    if (!visible) return

    // 초기 포커스: 주 액션 버튼
    enableButtonRef.current?.focus()

    const handleKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.preventDefault()
        handleDismiss()
        return
      }
      if (e.key === 'Tab' && dialogRef.current) {
        // 간이 focus trap: 모달 외부로 탭 이동 차단
        const focusable = dialogRef.current.querySelectorAll<HTMLElement>(
          'button:not([disabled]), [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
        )
        if (focusable.length === 0) return
        const first = focusable[0]!
        const last = focusable[focusable.length - 1]!
        if (e.shiftKey && document.activeElement === first) {
          e.preventDefault()
          last.focus()
        } else if (!e.shiftKey && document.activeElement === last) {
          e.preventDefault()
          first.focus()
        }
      }
    }

    document.addEventListener('keydown', handleKey)
    return () => document.removeEventListener('keydown', handleKey)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visible])

  const handleEnable = async () => {
    setSubmitting(true)
    setError(null)
    try {
      await subscribeToPush()
      setVisible(false)
    } catch (e) {
      const rawMessage = e instanceof Error ? e.message : ''

      // 인증 관련 에러 → 사용자 친화적 메시지
      if (
        rawMessage.includes('refresh token') ||
        rawMessage.includes('No refresh') ||
        rawMessage.includes('401') ||
        rawMessage.includes('로그인')
      ) {
        setError('로그인이 만료되었어요. 다시 로그인 후 알림을 켜주세요')
        setTimeout(() => setVisible(false), 3000)
        return
      }

      // 권한 거부
      if (rawMessage.includes('거부')) {
        setError('알림 권한이 거부되었어요. 브라우저 설정에서 허용으로 변경 후 다시 시도해주세요')
        safeLocalStorage.setItem(STORAGE_DISMISSED_AT, String(Date.now()))
        setTimeout(() => setVisible(false), 3000)
        return
      }

      // 기타 에러
      setError(rawMessage || '알림 설정에 실패했어요. 잠시 후 다시 시도해주세요')
    } finally {
      setSubmitting(false)
    }
  }

  const handleDismiss = () => {
    safeLocalStorage.setItem(STORAGE_DISMISSED_AT, String(Date.now()))
    setVisible(false)
  }

  if (!visible) return null

  return (
    <div
      className="fixed inset-0 z-50 flex items-end justify-center bg-black/40 px-4 pb-6 pt-20 sm:items-center sm:pb-0"
      role="dialog"
      aria-modal="true"
      aria-labelledby="push-prompt-title"
      onClick={(e) => {
        // 백드롭 클릭 시 닫기 (모달 내부 클릭은 stopPropagation)
        if (e.target === e.currentTarget) handleDismiss()
      }}
    >
      <div
        ref={dialogRef}
        className="relative w-full max-w-md rounded-2xl bg-white p-6 shadow-xl"
      >
        <button
          type="button"
          onClick={handleDismiss}
          aria-label="닫기"
          className="absolute right-3 top-3 flex h-8 w-8 items-center justify-center rounded-full text-slate-400 hover:bg-slate-100 hover:text-slate-600 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500"
        >
          <X className="h-4 w-4" />
        </button>

        <div className="flex h-12 w-12 items-center justify-center rounded-full bg-blue-50">
          <Bell className="h-6 w-6 text-blue-600" aria-hidden="true" />
        </div>

        <h2 id="push-prompt-title" className="mt-4 text-lg font-semibold text-slate-900">
          알림으로 더 빠르게 받아보세요
        </h2>
        <p className="mt-1.5 text-sm leading-relaxed text-slate-600">
          관심 시설의 평가등급, 정원 변경, 새 이벤트를 놓치지 않으려면 알림을 켜주세요.
          언제든 마이페이지에서 끌 수 있어요.
        </p>

        {error && (
          <p
            role="alert"
            className="mt-3 rounded-lg bg-red-50 px-3 py-2 text-xs text-red-700"
          >
            {error}
          </p>
        )}

        <div className="mt-5 flex justify-end gap-2">
          <button
            type="button"
            onClick={handleDismiss}
            disabled={submitting}
            className="rounded-xl px-4 py-2 text-sm font-medium text-slate-600 transition hover:bg-slate-100 disabled:opacity-50"
          >
            나중에
          </button>
          <button
            ref={enableButtonRef}
            type="button"
            onClick={handleEnable}
            disabled={submitting}
            className="rounded-xl bg-blue-600 px-4 py-2 text-sm font-medium text-white shadow-sm transition hover:bg-blue-700 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 disabled:opacity-50"
          >
            {submitting ? '설정 중...' : '알림 켜기'}
          </button>
        </div>
      </div>
    </div>
  )
}
